import React from 'react';
import { FileText, Download } from 'lucide-react';

/**
 * EmailAttachments - Component to display email attachments with download functionality
 */
const EmailAttachments = ({
  attachments = [],
  emailId,
  onDownload,
  onDownloadAll,
  showDownloadAll = true,
  className = ''
}) => {
  // Filter out inline attachments (images embedded in email body)
  const displayAttachments = attachments.filter(a => !a.isInline);

  if (!displayAttachments.length) return null;

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleDownload = (attachment) => {
    if (onDownload) {
      onDownload(emailId, attachment);
    }
  };

  const handleDownloadAll = () => {
    if (onDownloadAll) {
      onDownloadAll();
    } else if (onDownload) {
      displayAttachments.forEach(att => onDownload(emailId, att));
    }
  };

  return (
    <div className={`p-3 bg-gray-800/50 rounded-lg border border-gray-700 ${className}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-400">
          {displayAttachments.length} Attachment{displayAttachments.length > 1 ? 's' : ''}
        </span>
        {showDownloadAll && displayAttachments.length > 1 && (
          <button 
            onClick={handleDownloadAll}
            className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
            data-testid="download-all-attachments"
          >
            <Download size={12} />
            Download all
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {displayAttachments.map((att, attIdx) => (
          <button
            key={att.id || attIdx}
            onClick={() => handleDownload(att)}
            className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
            data-testid={`attachment-${attIdx}`}
          >
            <FileText size={16} className="text-blue-400" />
            <div className="text-left">
              <p className="text-xs font-medium text-white truncate max-w-[150px]">
                {att.name || att.filename || 'Attachment'}
              </p>
              {att.size && (
                <p className="text-xs text-gray-400">{formatFileSize(att.size)}</p>
              )}
            </div>
            <Download size={14} className="text-gray-400" />
          </button>
        ))}
      </div>
    </div>
  );
};

export default EmailAttachments;
